// PATH: src/lib/authSync.ts
// Synchronisation inter-UI (onglets/apps) des événements d'authentification.
// - Utilise BroadcastChannel si dispo, sinon fallback via localStorage + storage event.
// - Ne contient AUCUN état utilisateur (une seule vérité côté serveur).
// - À utiliser avec authCore.ts : sur réception d'un event, vos UIs appellent fetchMeCore() si nécessaire.

export type AuthEvent =
    | { type: "login" }                 // session établie (après /login OK)
    | { type: "logout" }                // session invalidée (après /logout OK)
    | { type: "user-updated"; keys?: string[] }; // profil modifié (ex: ["language"])

export type AuthEventHandler = (evt: AuthEvent) => void;

type Transport = "broadcastChannel" | "storage";

const CHANNEL = "tilmicode-auth";
const STORAGE_KEY = "__tilmicode_auth_sync__";

function nowIso() {
    return new Date().toISOString();
}

function randomId() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface AuthSync {
    /** Publier un événement aux autres UIs. */
    publish(evt: AuthEvent): void;
    /** S'abonner aux événements. Retourne un unsubscribe. */
    subscribe(handler: AuthEventHandler): () => void;
    /** Fermer/clean les ressources. */
    close(): void;
    /** Transport réellement utilisé. */
    transport(): Transport;
}

/**
 * Crée un canal de synchronisation. Safe à instancier une fois par app.
 * Exemple d’usage :
 *   const sync = createAuthSync();
 *   const unsub = sync.subscribe(async (e) => {
 *     if (e.type === "login" || e.type === "user-updated") {
 *       const me = await fetchMeCore();
 *       applyUser(me);
 *     }
 *     if (e.type === "logout") clearUser();
 *   });
 *   ...
 *   sync.publish({ type: "user-updated", keys: ["language"] });
 */
export function createAuthSync(): AuthSync {
    const instanceId = randomId();
    let bc: BroadcastChannel | null = null;
    let handlers = new Set<AuthEventHandler>();
    let closed = false;
    let usedTransport: Transport = "storage";

    // ------------------------------------------------------------
    // BroadcastChannel (si dispo)
    // ------------------------------------------------------------
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
        try {
            bc = new BroadcastChannel(CHANNEL);
            usedTransport = "broadcastChannel";
            bc.onmessage = (ev: MessageEvent<AuthEventPayload>) => {
                const payload = ev.data;
                if (!payload || payload.senderId === instanceId) return; // ignore echo
                dispatch(payload.event);
            };
        } catch {
            bc = null; // on tombera sur storage fallback
            usedTransport = "storage";
        }
    }

    // ------------------------------------------------------------
    // Fallback localStorage + storage event
    // ------------------------------------------------------------
    const onStorage = (e: StorageEvent) => {
        if (e.key !== STORAGE_KEY || !e.newValue) return;
        try {
            const payload: AuthEventPayload = JSON.parse(e.newValue);
            if (!payload || payload.senderId === instanceId) return;
            dispatch(payload.event);
        } catch {
            // ignore JSON parse errors
        }
    };
    if (!bc && typeof window !== "undefined") {
        window.addEventListener("storage", onStorage);
    }

    function publish(evt: AuthEvent) {
        if (closed) return;
        const payload: AuthEventPayload = {
            senderId: instanceId,
            at: nowIso(),
            event: evt,
        };

        if (bc) {
            try {
                bc.postMessage(payload);
                return;
            } catch {
                // si le canal a été fermé/bugué → fallback
            }
        }
        if (typeof window !== "undefined" && window.localStorage) {
            try {
                // Ajouter un nonce pour forcer le déclenchement du storage event
                const withNonce = { ...payload, nonce: randomId() };
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(withNonce));
                // Optionnel : clean après coup
                window.localStorage.removeItem(STORAGE_KEY);
            } catch {
                // ignore quota / private mode errors
            }
        }
    }

    function subscribe(handler: AuthEventHandler) {
        handlers.add(handler);
        return () => handlers.delete(handler);
    }

    function dispatch(evt: AuthEvent) {
        // Appel synchronisé pour garder un flux simple et prévisible
        handlers.forEach((h) => {
            try {
                h(evt);
            } catch {
                // ne bloque pas la boucle si un handler crash
            }
        });
    }

    function close() {
        if (closed) return;
        closed = true;
        handlers.clear();
        if (bc) {
            try { bc.close(); } catch { }
            bc = null;
        }
        if (typeof window !== "undefined" && !bc) {
            window.removeEventListener("storage", onStorage);
        }
    }

    function transport(): Transport {
        return usedTransport;
    }

    return { publish, subscribe, close, transport };
}

type AuthEventPayload = {
    senderId: string;
    at: string;          // ISO date pour debug/trace si besoin
    nonce?: string;      // utilisé par le fallback storage
    event: AuthEvent;
};
